/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useRef, useMemo, useCallback, Fragment } from 'react';
import { ResponsiveBar } from '@nivo/bar'
import { line } from "d3-shape";
//utils
import {CHART_SETTINGS, chartTableColors, formatYAxis} from '../../utils/charts';
// material
import LoadingState from '../states/loading/loading';
// styles
import injectSheet from 'react-jss';
import styles from './barchart_styles';

// z-axis line component
const Line = props => {
    /**
     * Z-Axis Line Component Props
     * d - composed the bar chart properties
     * length - length of the line. number of points. same number of x-axis items.
     * multiplier - used to multiply to the z axis data to match with the left/right y-axis.
     */
    const { d, length, multiplier, lineColor } = props;
    const zAxisLineColor = lineColor || '#062b75';

    const lineGenerator = line()
        .x(bar => d.xScale(bar.data.indexValue) + bar.width / 2)
        .y(bar => d.yScale((bar.data.data.z ? bar.data.data.z : 0) * multiplier));

    if(lineGenerator(d.bars) && length > 0) {
        const zAxisLines = lineGenerator(d.bars).split(',').slice(0, length + 1).join(',');
        return (
            <Fragment>
                <path
                    d={zAxisLines}
                    fill="none"
                    stroke={zAxisLineColor}
                    strokeWidth={2}
                    style={{ pointerEvents: "none !important" }}
                />
                {length === 1 && d.bars.map(bar => (
                    <circle
                        key={bar.key}
                        cx={d.xScale(bar.data.indexValue) + bar.width / 2}
                        cy={d.yScale((bar.data.data.z ? bar.data.data.z : 0) * multiplier)}
                        r={3}
                        fill={zAxisLineColor}
                        stroke={zAxisLineColor}
                        style={{ pointerEvents: "none" }}
                    />
                ))}
            </Fragment>
        );
    } else {
        return null;
    }
};

/* ========== ~~~~~~~~~~ BAR CHART  ~~~~~~~~~~ ========== */
const BarChart = injectSheet(styles)((props) => {
   
    const [initMax, setInitMax] = useState(null);
    const [currentBar, setCurrentBar] = useState({x: 0, y: 0, date: ''});
    const [locked, lock] = useState(false);
    const [currentBarParentAttr, setCurrentBarParentAttr] = useState(null); // get/set the current bar parent element for outline
    const [currentKeys, setCurrentKeys] = useState([]);
    const barChartRef = useRef(null);
    const hasZAxis = useMemo(() => props.data.filter(data => data.z).length > 0, [props.data]); // check the passed data if has z-axis 

    useEffect(() => {
        // initially set the max y-axis scale value
        if(!initMax) setInitMax(computeMaxY());
    }, [initMax]);

    useEffect(() => {
        // set the max y-axis scale value after changing the selected period/rollups
        if(!props.loading) setInitMax(computeMaxY());
    }, [props.period, props.loading, props.data]);

    useEffect(() => {
        const dataKeys = props.data.length > 0 ? Object.keys(props.data[0]) : null;
        if(dataKeys && currentKeys.length != dataKeys.length) {
            // update max y-axis scale value after changing the enabled products
            setInitMax(computeMaxY());
            // show current bar outline after changing the enabled products
            showBarOutline(currentBarParentAttr);
            // set current keys to compare to previous keys
            setCurrentKeys(dataKeys);
        }
    }, [props.data]);   

    const keysHasNoTotal = props.keys.filter(key => key.toLowerCase().includes('total')).length === 0

    const computeMaxY = useCallback(() => {
        const maxYs = [];
        props.data.map(dataset => {
            let sum = 0; 
            if(props.zAxisId) {
                // Compute the selected products max y-axis value when it has z-axis. Exlude the total and the z-axis field as well.
                props.keys.filter(key => !key.toLowerCase().includes(props.zAxisId) && !key.toLowerCase().includes('total') && !isNaN(dataset[key])).forEach(key => sum += dataset[key]); 
            } else {
                // Compute the selected products max y-axis without z-axis
                // if keys is only 1, total it
                if(keysHasNoTotal) {
                    props.keys.filter(key =>!isNaN(dataset[key])).forEach(key => sum += dataset[key]);
                } else {
                    props.keys.filter(key => key.toLowerCase().includes('total') && !isNaN(dataset[key])).forEach(key => sum += dataset[key]);
                }
            }
            maxYs.push(sum);
        });
    
        let maxY = Math.max(...maxYs.map(maxY => maxY), 0);

        if(hasZAxis || props.zAxisId) {
            const baseUnit = getMaxBaseUnit(maxY);
            const baseUnitCeil = baseUnit * 10;
            const remainder = maxY % baseUnit;
            maxY = (maxY - remainder) + (baseUnit);
            if(baseUnitCeil !== maxY && (baseUnitCeil / 2) !== maxY && (baseUnitCeil / 5) !== maxY) {
                if(maxY < (baseUnitCeil / 5)) maxY = baseUnitCeil / 5;
                else if(maxY < (baseUnitCeil / 2)) maxY =  (baseUnitCeil / 2)
                else maxY = baseUnitCeil
            }
        } else {
            maxY = maxY * 1.5;
        }
        
        maxY = maxY < 5 ? 10 : maxY;

        return maxY;
    }, [props.data]);

    const getMaxBaseUnit = max => {
        const zeroDigitsCount = max.toString().length - 1;
        let zeroString = '';
        for(var i = 0; i < zeroDigitsCount; i++)  zeroString += '0';
        return parseInt(`1${zeroString}`);
    }

    useEffect(() => {
        if(props.reset && locked) {
            lock(false);
            hideBarOutline();
        }
    }, [props.reset]);

    useEffect(() => {
        if(locked) {
            document.addEventListener('mousedown', handleOutsideClick, false);
        } 
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick, false);
        }
    }, [locked]);

    useEffect(() => {
        const applyOpacity = () => {
            const dateElements = Array.from(barChartRef.current?.querySelectorAll("g text") || []).filter(el => {
                const text = el.textContent
                const dates = props.data.map(d => d.date)
                return dates.includes((text || '').trimEnd())
            });

            if (dateElements.length > CHART_SETTINGS.numOfTicksAllowed && props.period === 'daily' && !props.loading) {
                dateElements.forEach((element, index) => {
                    if (index % 5 === 0) {
                        element.style.opacity = '1';
                    } else {
                        element.style.opacity = '0';
                    }
                });
            }
        };

        // Delay the execution (adjust as needed)
        setTimeout(applyOpacity, 10);
    }, [props.period, props.loading]);

    const handleOutsideClick = e  => {
         // ignore clicks on the chart toggles
        if(e.target.classList.value.includes('charttoggle')) return null;
        lock(false);
        hideBarOutline();
        setCurrentBar({x: 0, y: 0, date: ''});
        props.onMouseLeave();
    }


    const showBarOutline = attr => {
        for (const a of barChartRef.current.querySelectorAll("g")) {
            if (a.hasAttribute('transform') && a.getAttribute('transform').includes(`${attr},`)) {
                // a.style.outline = '10px solid rgb(218, 238, 255)'
                if(a.childNodes.length > 0) {
                    a.childNodes[0].style.strokeWidth = 6;
                    // a.childNodes[0].style.stroke = 'rgb(218, 238, 255)'; // light color
                    a.childNodes[0].style.stroke = 'rgb(135, 195, 250)'; // dark color
                    // a.childNodes[0].style.transition = 'all .2s ease-in-out 0s';
                    a.childNodes[0].style.cursor = 'pointer';
                }
            }
        }
    }

    const hideBarOutline = () => {
        for (const a of barChartRef.current.querySelectorAll("g")) {
            // a.style.outline = 'none';
            if(a.childNodes.length > 0) a.childNodes[0].style.strokeWidth = 0;
        }
        for (const a of barChartRef.current.querySelectorAll("text")) {
            a.style.fontWeight = 'normal';
            a.style.outline = 'none';
        }
        setCurrentBarParentAttr(null);
        if (props.onMouseLeavePerBar) {
            props.onMouseLeavePerBar();
            handleMouseLeave();
        };
    }

    const handleClick = (data, event) => {
        let parentAttr = event.target.parentNode.getAttribute('transform');
        parentAttr = parentAttr.indexOf(',') ? parentAttr.split(',')[0] : '';
        setCurrentBarParentAttr(parentAttr);
        showBarOutline(parentAttr)
        lock(true);
        props.onHover(data.data)
    }

    const handleHover = data => {
        if(!locked) {
            showBarOutline('translate(' + data.x)
            if(data.x != currentBar.x || data.data.date != currentBar.date) {
                setCurrentBar({x: data.x, y: data.y, date: data.data.date});
                props.onHover && props.onHover(data);
            }
        }
        return null;
    }

    const handleMouseLeave = () => {
        if(!locked && props.onMouseLeave) {
            setCurrentBar({x: 0, y: 0, date: ''});
            props.onMouseLeave();
        }
    }

    const formattedData = useMemo(() => 
        [...props.data].map((data, index) => {
            // To allow duplicate x-axis labels // TODO: Refactor this
            let spaces = '';
            for(var i = 0; i < index; i++) spaces += ' ';
            const d = {
                ...data,
                [props.barTotalKey ? props.barTotalKey : 'Total']: 0, // Setting this to 0 so it won't get rendered in the bar graph
                date: data && data.date ? data.date.concat(spaces) : index
            }
            return d;
        })
    , [props.data]);

    const defaultProps = {
        enableLabel: false,
        data: formattedData,
        keys: props.keys,
        indexBy: props.indexBy,
        valueScale: { type: 'linear' },
        indexScale: { type: 'band', round: true },
        valueFormat: { format: '', enabled: true },
        gridYValues: hasZAxis || props.zAxisId ? 7 : 5,
        colors: props.colors ? props.colors : ['#fff' ,...chartTableColors], // TODO: Hotfix for monetization barchart. Update this for comment analytics report
        borderColor: { from: 'color', modifiers: [ [ 'darker', 1.6 ] ] },
        // labelSkipWidth: 12,
        // labelSkipHeight: 12,
        labelTextColor: { from: 'color', modifiers: [ [ 'darker', 1.6 ] ] },
        maxValue: initMax && !Number.isNaN(initMax) ? initMax : 'auto',
        axisTop: null,
        theme: {
            fontSize: 10
        }
    }

    let widthPaddingProps = props.data.length > 10 ? {
        // width: 2000, TODO: previous implementation when data is more than 10 scroll the barchart
        padding: props.data.length > 20 ? 0.12 : 0.5
    } : { padding: 0.6 }


    if(props.data.length > CHART_SETTINGS.dailyRollupThreshold) {
        widthPaddingProps = {
            width: 3000,
            padding: 0.12
        }
    }

    const calculateTickValues = () => {
        let tickValues = [];
        for(var i = 0; i < 6; i++) {
            tickValues.push((initMax / 5) * i);
        }
        return tickValues;
    }
    const renderBarGraph = () => {
        // Get the max value of the left y-axis (Used for computing the common z-axis scale - right axis)
        const leftYAxisMax = max => {
            if(max > 0) {
                const zeroCount = parseInt(max).toString().length - 1;
                if(zeroCount >= 0) {
                    const baseInt = max.toString()[0];
                    let baseMax = baseInt;
                    for(var i = 0; i < zeroCount; i++) {
                        baseMax += '0';
                    }
                    return parseInt(baseMax);
                }
            } else {
                return 0;
            }
        }

        const formatTimeZAxis = num => {
            const mins = Math.floor(num / 60);
            let formattedMins =  mins > 0 ? `${mins}m` : '';
            formattedMins = mins >= 60 ? `${(mins / 60).toFixed(1)}h` : formattedMins;
            return num > 0 ? `${formattedMins}` : 0;
        }

        let maxZAxisValue = hasZAxis ? parseInt((Math.max(...props.data.map(data => data.z), 0))) : 0; // get the data max z value - same way we compute the left y-axis max
        // maxZAxisValue = hasZAxis ? (maxZAxisValue < 5 ? Math.ceil(maxZAxisValue * 2) : Math.ceil(maxZAxisValue * 1.5)) : 0; // add extra padding to the z-axis value
    
        if(hasZAxis || props.zAxisId) {
            const baseUnit = getMaxBaseUnit(maxZAxisValue);
            const remainder = maxZAxisValue % baseUnit;
            maxZAxisValue = (maxZAxisValue - remainder) + (baseUnit);

            if(props.maxZAxisValue) {
                // set the max z-axis value based on the passed property
                // add a fallback if the passed max z-axis value is less than the actual max z-axis value
                maxZAxisValue = props.maxZAxisValue > maxZAxisValue ? props.maxZAxisValue : maxZAxisValue; 
            } else {
                switch(props.zType) {
                    case 'percentage':
                        maxZAxisValue = 100;
                        break;
                    case 'time':
                        maxZAxisValue = maxZAxisValue <= 3600 ? 3600 : (maxZAxisValue - (maxZAxisValue % 3600));
                        break;
                    default:
                        maxZAxisValue =  maxZAxisValue < 10 ? 10 : maxZAxisValue * 2;
                        break;
                }
            }
        }
        
        // Common denominator to compute the z-axis scale values (right axis). See ResponsiveBar axisRight format below. 
        // Also used as a multiplier of z-axis line values. See Line component multiplier. 
        const zAxisDenominator =  hasZAxis || props.zAxisId ? (leftYAxisMax(initMax) / maxZAxisValue) : 0;
        
        if(props.loading) return <div style={{ height: 400,  width: '100%', position: 'relative'}}><LoadingState /> </div>
        return (
            <div style={{ height: 400,  width: '100%', position: 'relative'}}>
                <ResponsiveBar 
                    {...widthPaddingProps}
                    {...defaultProps}
                    margin={{ top: 5, right: props.zAxisId ? 50 : 25, bottom: 60, left: props.zAxisId ? 70 : 80 }}
                    axisLeft={{
                        orient: 'left',
                        tickValues: props.zAxisId ? calculateTickValues() : 5,
                        tickSize: 0,
                        tickPadding: 20,
                        tickRotation: 0,
                        legend: null,
                        format: value => formatYAxis(value, props.yType)
                    }}
                    axisBottom={{
                        orient: 'bottom',
                        tickSize: 0,
                        tickPadding: 20,
                        tickRotation: props.data.length > 10 ? 60 : 0
                    }}
                    axisRight={{
                        orient: 'left',
                        tickValues: props.zAxisId ? calculateTickValues() : 5,
                        tickSize: 0,
                        tickPadding: 20,
                        tickRotation: 0,
                        legend: null,
                        format: value => {
                            if(hasZAxis) {
                                const isDecimal = maxZAxisValue < 5;
                                const axisValue = value / zAxisDenominator;
                                // To avoid infinity or NaN values
                                const decimalAxisValue = axisValue < 1 ? `${axisValue.toFixed(2)}`.replace(/^0./, '.') : axisValue.toFixed(1);
                                const zAxisValue = zAxisDenominator > 0 ? (isDecimal ? decimalAxisValue : Math.round(axisValue)) : 0; 
                                // TODO: Indicate zType prop to render the correct z-axis format
                                switch(props.zType) {
                                    case 'time':
                                        return formatTimeZAxis(zAxisValue);
                                    case 'percentage':
                                        return `${zAxisValue} %`;
                                    default:
                                        return zAxisValue;
                                }
                            } else {
                                return '';
                            }
                        }
                    }}
                    tooltip={handleHover}
                    onClick={handleClick}
                    onMouseLeave={() => !locked ? hideBarOutline() : null }
                    layers={['grid', 'axes', 'bars', hasZAxis ? d => <Line d={d} multiplier={zAxisDenominator} length={formattedData.length} lineColor={props.zAxisColor} /> : null]}
                />
            </div>
        );
    }

    return (
        <div ref={barChartRef} className={`barchart ${props.classes.barchart}`} onMouseLeave={handleMouseLeave}>
            { renderBarGraph() }
        </div>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default BarChart;